<template>
  <div class="w-full bg-slate-50 min-h-screen p-4 md:p-8" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="flex items-center justify-between mb-8">
        <div class="flex items-center gap-4">
          <v-btn icon @click="$router.push('/supplies-manager')" class="bg-white border border-slate-200 shadow-sm rounded-lg">
            <v-icon color="slate-600">mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h1 class="text-2xl font-black text-slate-800 tracking-tight">
              {{ id ? 'Editar Abastecimento' : 'Novo Abastecimento Administrativo' }}
            </h1>
            <p class="text-sm text-slate-500 font-medium mt-1">
              {{ $route.name }} - Preencha as informações necessárias
            </p>
          </div>
        </div>
      </div>

      <!-- MAIN CARD -->
      <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <!-- Divisor Escuro -->
        <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
          <v-icon color="orange" small>mdi-form-select</v-icon>
          <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
            DETALHES DO ABASTECIMENTO
          </h2>
        </div>

        <div class="p-6">
          <v-form ref="form" v-model="valid" lazy-validation>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              <!-- Left Column: Primary Selections -->
              <div class="space-y-6">
                <div class="bg-slate-50 p-6 rounded-xl border border-slate-200 space-y-4">
                  <h3 class="text-xs font-black text-slate-400 uppercase tracking-widest">Entidades</h3>
                  
                  <v-autocomplete
                    label="Fornecedor:"
                    :rules="fieldRules"
                    :items="providers"
                    @change="loadItems(data.provider_id)"
                    item-value="provider_id"
                    item-text="provider_name"
                    v-model="data.provider_id"
                    outlined
                    dense
                    class="bg-white rounded-lg"
                  />

                  <v-autocomplete
                    label="Item / Combustível:"
                    :rules="fieldRules"
                    :items="items"
                    item-value="item_id"
                    item-text="item_name"
                    v-model="data.item_id"
                    outlined
                    dense
                    class="bg-white rounded-lg"
                    no-data-text="Nenhum item encontrado"
                  >
                    <template slot="selection" slot-scope="{ item }">
                      <span class="text-sm font-bold text-slate-700">{{ item.item_description_desc }}</span>
                      <span class="text-[10px] text-slate-500 ml-2">(R$: {{ realMask(item.item_price.toFixed(2)) }})</span>
                    </template>
                    <template slot="item" slot-scope="{ item }">
                      <div class="flex items-center justify-between w-full">
                        <span class="text-sm font-medium">{{ item.item_description_desc }}</span>
                        <span class="text-[10px] font-bold text-emerald-600">R$: {{ realMask(item.item_price.toFixed(2)) }}</span>
                      </div>
                    </template>
                  </v-autocomplete>

                  <v-autocomplete
                    label="Motorista:"
                    :items="drivers"
                    item-value="driver_id"
                    item-text="driver_name"
                    v-model="data.driver_id"
                    outlined
                    dense
                    class="bg-white rounded-lg"
                    append-icon="mdi-plus"
                    @click:append="showForm = true"
                  />
                </div>
              </div>

              <!-- Right Column: Values and Justification -->
              <div class="space-y-6">
                <div class="bg-slate-50 p-6 rounded-xl border border-slate-200 space-y-4">
                  <h3 class="text-xs font-black text-slate-400 uppercase tracking-widest">Valores e Justificativa</h3>
                  
                  <div class="grid grid-cols-2 gap-4">
                    <v-text-field
                      label="Litros Autorizados"
                      v-model="data.supply_authorized_liters"
                      v-mask-decimal.br="3"
                      outlined
                      dense
                      class="bg-white rounded-lg"
                      :disabled="disableLitros"
                    />
                    <v-text-field
                      label="Valor Autorizado (R$)"
                      v-model="data.supply_authorized_value"
                      v-mask-decimal.br="2"
                      outlined
                      dense
                      class="bg-white rounded-lg"
                      :disabled="disableValor"
                    />
                  </div>

                  <v-textarea
                    label="Justificativa"
                    v-model="data.justificativa"
                    :rules="fieldRules"
                    rows="3"
                    outlined
                    dense
                    class="bg-white rounded-lg"
                    auto-grow
                  />
                </div>
              </div>

            </div>

            <!-- Footer Actions -->
            <div class="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-slate-100">
              <v-btn
                text
                large
                color="secondary"
                @click="$router.push('/supplies-manager')"
                class="rounded-lg font-bold"
              >
                Cancelar
              </v-btn>
              <v-btn
                depressed
                large
                color="primary"
                :disabled="!valid && loader"
                :loading="loader"
                @click="validate"
                class="rounded-lg font-bold px-12 shadow-md"
              >
                Confirmar
              </v-btn>
            </div>
          </v-form>
        </div>
      </div>
    </div>

    <!-- MODAL NOVO MOTORISTA -->
    <v-dialog v-model="showForm" width="500">
      <v-card class="rounded-xl overflow-hidden shadow-2xl">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-account-plus</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
              Cadastrar Novo Motorista
            </h2>
          </div>
          <v-btn icon dark small @click="showForm = false; driver_name = ''">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="p-6">
          <v-form @submit.prevent="newDriver" class="space-y-6">
            <v-text-field
              :rules="fieldRules"
              label="Nome do motorista:"
              v-model="driver_name"
              outlined
              dense
              class="mt-2"
            />
            
            <div class="flex items-center justify-end gap-3 border-t border-slate-100 pt-6">
              <v-btn text color="secondary" @click="[showForm = false, driver_name = '']">Cancelar</v-btn>
              <v-btn
                depressed
                color="primary"
                :disabled="!driver_name"
                :loading="loader"
                @click="newDriver"
                class="rounded-lg font-bold px-8"
              >Confirmar</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    
    <!-- MODAL SENHA -->
    <v-dialog v-model="showPassword" width="350" persistent>
      <v-card class="rounded-2xl overflow-hidden shadow-2xl border-2 border-indigo-100">
        <div class="bg-indigo-600 p-6 text-center">
          <v-icon color="white" large class="mb-2">mdi-lock-check</v-icon>
          <h2 class="text-white font-black text-xl tracking-tight leading-tight uppercase">
            Senha Autorizada!
          </h2>
        </div>
        
        <v-card-text class="p-8 text-center bg-white">
          <div class="space-y-6">
            <div class="bg-slate-50 p-6 rounded-2xl border-2 border-dashed border-slate-200">
              <input
                readonly
                class="text-4xl font-black text-indigo-700 text-center tracking-widest w-full bg-transparent border-0 outline-none"
                ref="copiar"
                :value="password"
              />
            </div>
            
            <v-btn
              :color="copiado ? 'emerald' : 'indigo'"
              depressed
              dark
              block
              large
              @click="copy"
              class="rounded-xl font-black text-sm tracking-widest h-14"
            >
              <v-icon left size="20">{{ copiado ? 'mdi-check-circle' : 'mdi-content-copy' }}</v-icon>
              {{ copiado ? "COPIADO COM SUCESSO" : "COPIAR SENHA" }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar multi-line :color="snack.color" v-model="snack.show" transition="fade-transition">
      {{snack.message}}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
export default {
  props: ["id"],
  mixins: [Helpers],
  data: () => ({
    valid: false,
    loader: false,
    showForm: false,
    showPassword: false,
    copiado: false,
    password: "",
    providers: [],
    drivers: [],
    vehicles: [],
    items: [],
    driver_name: "",
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      supply_id: null,
      supply_authorized_liters: "0",
      supply_authorized_value: "0",
      supply_status: "PRE-AUTORIZADO",
      supply_adjusted: false,
      supply_extra: true,
      supply_liters_stocked: false,
      supply_billed: false,
      vehicle_id: null,
      driver_id: null,
      item_id: null,
      prefecture_id: null,
      provider_id: null,
      justificativa: null,
    },
  }),
  created() {
    this.Helpers;
    this.$http
      .get(`/providers/prefecture/${this.$store.getters.getUserData.table_id}`)
      .then((r) => (this.providers = r.data));
    this.$http
      .get(`/supplies/drivers/${this.$store.getters.getUserData.table_id}`)
      .then((r) => (this.drivers = r.data));
    this.$http
      .get(`/supplies/vehicles/${this.$store.getters.getUserData.table_id}`)
      .then((r) => (this.vehicles = r.data));
    this.data.prefecture_id = this.$store.getters.getUserData.table_id;

    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`supplies/single/${this.id}`).then((r) => {
        this.data = r.data[0];
        this.$http
          .get(`/supplies/providers/${r.data[0].provider_id}`)
          .then((i) => {
            this.items = i.data;
            this.dialog = true;
          });
        delete this.data.datehour;
        delete this.data.supply_liters_stocked;
        delete this.data.supply_price_day;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  computed: {
    disableLitros() {
      return parseFloat(this.data.supply_authorized_value.replace(',', '.')) > 0;
    },
    disableValor() {
      return parseFloat(this.data.supply_authorized_liters.replace(',', '.')) > 0;
    }
  },
  methods: {
    copy() {
      var copyTextarea = this.$refs.copiar;
      copyTextarea.select();
      try {
        var successful = document.execCommand("copy");
        if (successful) {
          this.copiado = true;
          setTimeout(() => {
            this.showPassword = false;
            this.copiado = false;
            this.$router.push("/supplies-manager");
          }, 1000);
        } else {
          alert("não copiado!");
        }
      } catch (err) {
        alert(
          "Opa, Não conseguimos copiar o texto, é possivel que o seu navegador não tenha suporte, tente usar Crtl+C."
        );
      }
    },
    loadItems(provider_id) {
      this.$http
        .get(`/supplies/providers/${provider_id}`)
        .then((r) => (this.items = r.data));
    },
    newDriver() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .post("/supplies/drivers", {
          driver_name: this.driver_name,
          prefecture_id: this.data.prefecture_id,
        })
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          this.$http.get("/drivers").then((d) => {
            this.drivers = d.data;
            const driverItems = d.data.filter((f) =>
              f.driver_name === this.driver_name ? f : null
            );
            console.log(driverItems[0].driver_id);
            this.data.driver_id = driverItems[0].driver_id;
            this.showForm = false;
          });
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = e.message;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    validate() {
      this.snack.show = true;
      this.loader = true;
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message =
          "Atencão um ou mais campos precisam ser preenchidos!";
        this.loader = false;
      }
    },
    checkFields() {
      if (this.data.supply_authorized_value) {
        this.data.supply_authorized_liters = 0;
      } else {
        this.data.supply_authorized_value = 0;
      }
    },
    createOrUpdate() {
      this.loader = true;
      // this.checkFields();
      if (this.id) {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .put(`/supplies/single/${this.id}`, this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.loader = true;
            setTimeout(() => {
              this.$router.push("/supplies-manager");
            }, 1500);
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$store.dispatch("setLoader", this.loader);
        this.data.is_autorized = true;
        this.data.authorizer = this.$store.getters.getUserData.user_login;
        this.$http
          .post("/supplies/single", this.data)
          .then((r) => {
            this.showPassword = true;
            this.$http
              .get(`/supplies/single/${r.data.idInserted}`)
              .then((r) => (this.password = r.data[0].supply_code));
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style>
.title {
  text-transform: uppercase;
  font-weight: 400;
}

.flexd {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}
.flexd p {
  margin: 10px 0;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
